import React, { useMemo, useRef, useState, useCallback, memo } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, ScrollView, FlatList, Alert, RefreshControl, KeyboardAvoidingView, Platform, useWindowDimensions } from 'react-native';
import { Audio } from 'expo-av';
import Button from '@components/UI/Button/Button';
import * as Sharing from 'expo-sharing';
import { AntDesign, MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Dialog, Label, Button as TamaButton, SizableText, XStack, YStack, Adapt, Sheet, Popover, Spinner, Separator } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import LottieView from 'lottie-react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AI_API_URL } from '@api/constant';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import MeetingItem from './Components/MeetingItem';
import { transcribeAudio } from '@api/aiApi';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { BlurView } from '@constants/Themed';
import globalStyles from '@stylesheet';

// The handleTranscribeRequest function using the transcribeAudio function
async function handleTranscribeRequest(file, duration: { startTime: number, endTime: number }, details) {
  return new Promise(async (resolve, reject) => {
    const formData = new FormData();
   return true;
  });
}

export default function AppVoice({ widget }: { widget?: boolean }) {
  const [recording, setRecording] = React.useState<Audio.Recording | undefined>();
  const [recordings, setRecordings] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [myRecordingsOpen, setMyTemplatesOpen] = React.useState(true);
  const [meetingsOpen, setMeetingsOpen] = React.useState(true);
  const [sortOption, setSortOption] = React.useState<'date-new' | 'date-old' | 'duration-short' | 'duration-long' | 'title-az' | 'title-za'>('date-new');
  const theme = themeSelector();
  const timerRef = React.useRef(null);
  const [playingIndex, setPlayingIndex] = React.useState(null);
  const popRef = useRef<Popover>(null)
  const [timings, setTimings] = useState({ startTime: undefined, endTime: undefined });
  const formRef = useRef(null);
  const { height } = useWindowDimensions();

  const { isLoading, data, error, isFetching, refetch } = useQuery({
    queryKey: ['meetings'],
    queryFn: async () => {
      const data = await apis.getMeetings()
        .then(res => {
          return res.meetings
        });
      return data;
    }
  });
  const memoizedData = useMemo(() => {
    if (!data) return [];

    const sortedData = [...data];

    switch (sortOption) {
      case 'date-new':
        return sortedData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'date-old':
        return sortedData.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'duration-short':
        return sortedData.sort((a, b) => (a.duration || 0) - (b.duration || 0));
      case 'duration-long':
        return sortedData.sort((a, b) => (b.duration || 0) - (a.duration || 0));
      case 'title-az':
        return sortedData.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'title-za':
        return sortedData.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      default:
        return sortedData.reverse();
    }
  }, [data, sortOption])

  const { isLoading: loading, data: schema, error: schemaError } = useQuery({
    queryKey: ['meeting-schema'],
    queryFn: async () => {
      return await apis.getMeetingSchema();
    }
  });

  async function startRecording() {
    try {
      const permission = await Audio.requestPermissionsAsync();

      if (permission.status === "granted") {
        await Audio.setAudioModeAsync({
          allowsRecordingIOS: true,
          playsInSilentModeIOS: true,
        });
        const { recording } = await Audio.Recording.createAsync(
          Audio.RecordingOptionsPresets.HIGH_QUALITY
        );

        //count down timer
        let count = 0;
        timerRef.current = setInterval(() => {
          count++;
          setMessage(`Recording ${count} seconds`);
        }, 1000);
        setRecording(recording);

      } else {
        setMessage("Please grant permission to app to access microphone");
      }
    } catch (err) {
      console.error('Failed to start recording', err);
    }
  }

  async function stopRecording() {
    if (!recording) return;
    
    setRecording(undefined);
    await recording.stopAndUnloadAsync();
    await Audio.setAudioModeAsync({
      allowsRecordingIOS: false,
      playsInSilentModeIOS: true,
    });

    let updatedRecordings = [...recordings];
    const { sound, status } = await recording.createNewLoadedSoundAsync();
    
    // Safely access durationMillis
    const durationMillis = (status as any)?.durationMillis || 0;
    
    updatedRecordings.push({
      sound: sound,
      startTime: new Date().getTime() - durationMillis,
      endTime: new Date().getTime(),
      duration: getDurationFormatted(durationMillis),
      file: recording.getURI()
    });
    setRecordings(updatedRecordings);
    clearInterval(timerRef.current);
  }

  function getDurationFormatted(millis) {
    const minutes = millis / 1000 / 60;
    const minutesDisplay = Math.floor(minutes);
    const seconds = Math.round((minutes - minutesDisplay) * 60);
    const secondsDisplay = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutesDisplay}:${secondsDisplay}`;
  }

  const EmptyRecordingsComponent = useCallback(() => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark) }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark]
    }>
      <YStack alignItems="center" space="$3" padding="$4">
        <MaterialIcons 
          name="mic-none" 
          size={48} 
          color={theme ? '#6b7280' : '#9ca3af'} 
        />
        <Text style={{ 
          color: theme ? '#6b7280' : '#9ca3af', 
          textAlign: 'center',
          fontSize: 16,
          fontWeight: '600'
        }}>
          No recordings available
        </Text>
        <Text style={{ 
          color: theme ? '#64748b' : '#94a3b8', 
          textAlign: 'center',
          fontSize: 14,
          lineHeight: 20
        }}>
          Start recording to create your first meeting note
        </Text>
      </YStack>
    </View>
  ), [theme]);

  const RecordingItem = useCallback(({ recordingLine, index }) => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), width: 280, marginHorizontal: 8, padding: 16 }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { width: 280, marginHorizontal: 8, padding: 16 }]
    }>
      {/* Local Storage Badge */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 6,
        backgroundColor: theme ? '#fef3c7' : '#78350f',
        borderRadius: 8,
        marginBottom: 12,
        alignSelf: 'flex-start'
      }}>
        <MaterialIcons name="phonelink" size={14} color={theme ? '#d97706' : '#fbbf24'} />
        <Text style={{ 
          color: theme ? '#d97706' : '#fbbf24',
          fontSize: 11,
          fontWeight: '600'
        }}>
          Stored on Device
        </Text>
      </View>

      {/* Recording Info */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{
          color: theme ? "#1e293b" : "#f1f5f9",
          fontSize: 18,
          fontWeight: '700',
          marginBottom: 6
        }}>
          Recording {index + 1}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <MaterialIcons name="schedule" size={16} color={theme ? '#64748b' : '#94a3b8'} />
          <Text style={{ 
            color: theme ? "#64748b" : "#94a3b8", 
            fontSize: 14 
          }}>
            {recordingLine.duration}
          </Text>
        </View>
      </View>

      {/* Action Buttons */}
      <View style={{ gap: 10 }}>
        <TouchableOpacity 
          style={Platform.OS === 'web'
            ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, gap: 8 }
            : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, gap: 8 }]
          }
          onPress={() => {
            if (playingIndex === index) {
              setPlayingIndex(null);
              recordingLine.sound.stopAsync()
            } else {
              setPlayingIndex(index);
              recordingLine.sound.replayAsync()
            }
          }}
        >
          <MaterialIcons
            name={playingIndex === index ? "pause" : "play-arrow"}
            size={20}
            color="white"
          />
          <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
            {playingIndex === index ? 'Pause' : 'Play'}
          </Text>
        </TouchableOpacity>

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TouchableOpacity 
            style={Platform.OS === 'web'
              ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark), flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 10 }
              : [globalStyles.modernActionButton, theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark, { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 10 }]
            }
            onPress={() => Sharing.shareAsync(recordingLine.file)}
          >
            <MaterialIcons name="share" size={20} color="white" />
          </TouchableOpacity>
          
          <Dialog modal>
            <Dialog.Trigger asChild>
              <TouchableOpacity
                style={Platform.OS === 'web'
                  ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark), flex: 2, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 10, gap: 6 }
                  : [globalStyles.modernActionButton, theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark, { flex: 2, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 10, gap: 6 }]
                }
              >
                <MaterialIcons name="cloud-upload" size={20} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
                  Upload
                </Text>
              </TouchableOpacity>
            </Dialog.Trigger>
            <Adapt platform="touch">
              <Sheet animation="medium" modal dismissOnSnapToBottom>
                <Sheet.Frame 
                  contain='content' 
                  flex={1} 
                  marginInline={"5%"} 
                  maxHeight={"80%"} 
                  maxWidth={"90%"} 
                  alignSelf='center' 
                  alignContent='center' 
                  justifyContent='center' 
                  shadowRadius={20} 
                  shadowOpacity={.2} 
                  padding="$4" 
                  gap="$4"
                >
                  <Adapt.Contents />
                </Sheet.Frame>
              </Sheet>
            </Adapt>
            <Dialog.Portal zIndex={1000}>
              <Dialog.Overlay
                opacity={0}
                animation="quick"
                maxHeight={"80%"}
                style={{ zIndex: 1000, padding: 10 }}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              />
              <KeyboardAvoidingView
                behavior='padding'
                keyboardVerticalOffset={180}
              >
                <Dialog.Content
                  bordered
                  elevate
                  animation={[
                    'quick',
                    {
                      opacity: {
                        overshootClamping: true,
                      },
                    },
                  ]}
                  enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
                  exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
                  x={0}
                  scale={1}
                  style={{
                    padding: 40,
                    minWidth: 400,
                    maxHeight: "80%",
                    gap: 20,
                    borderRadius: 24,
                    elevation: 12,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 12 },
                    shadowOpacity: 0.15,
                    shadowRadius: 32,
                  }}
                  opacity={1}
                  y={0}
                >
                  <YStack gap={20} flex={1}>
                    <Dialog.Title
                      fontSize={32}
                      fontWeight={'bold'}
                      style={{ color: theme ? "#1e293b" : "#f1f5f9" }}
                    >
                      Upload your meeting
                    </Dialog.Title>
                    <Dialog.Description
                      style={{
                        color: theme ? "#64748b" : "#94a3b8",
                        fontSize: 16,
                        lineHeight: 22
                      }}
                    >
                      I will help quickly summarise your meeting and generate some actions for you
                    </Dialog.Description>
                    <Separator marginVertical={20} />
                    <YStack flex={1} gap={20}>
                      <YStack minHeight={350} gap={20}>
                        <View style={{ minHeight: 400 }}>
                          <GenerateSchemaForm 
                            ref={formRef} 
                            bottomPadding={100} 
                            customStyling={{ minHeight: 200, marginLeft: -20 }} 
                            schema={[
                              { type: 'string', title: 'Title' },
                              { title: "Participants", type: "user_select" },
                              { title: "Location", type: "address", ref: 'location' }
                            ]}
                            hideButton
                          />
                        </View>
                      </YStack>
                      <XStack position='absolute' bottom={"0%"} gap="$3" justifyContent="flex-end">
                        <Dialog.Close displayWhenAdapted asChild>
                          <TamaButton>Cancel</TamaButton>
                        </Dialog.Close>
                        <TamaButton 
                          onPress={async () => {
                            console.log('form values are', formRef.current.values);
                            await handleTranscribeRequest(recordingLine, timings, formRef.current.values).then(() => {
                              popRef.current?.close()
                            });
                          }} 
                          theme="active"
                        >
                          Generate
                        </TamaButton>
                      </XStack>
                    </YStack>
                  </YStack>
                </Dialog.Content>
              </KeyboardAvoidingView>
            </Dialog.Portal>
          </Dialog>
        </View>
      </View>

      {/* Upload Hint */}
      <View style={{
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: theme ? '#e2e8f0' : '#334155'
      }}>
        <Text style={{
          color: theme ? "#64748b" : "#94a3b8",
          fontSize: 11,
          textAlign: 'center',
          lineHeight: 16
        }}>
          Upload to get transcript & AI summary
        </Text>
      </View>
    </View>
  ), [theme, playingIndex, timings, formRef, popRef]);

  const renderRecordingItem = useCallback(({ item, index }) => (
    <RecordingItem 
      recordingLine={item}
      index={index}
    />
  ), [RecordingItem]);

  const ModernRecorder = useCallback(() => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), ...localStyles.modernRecorderCard }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, localStyles.modernRecorderCard]
    }>
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16
      }}>
        <TouchableOpacity disabled={!!recording}>
          <MaterialCommunityIcons 
            name="microphone" 
            size={24} 
            color={recording ? (theme ? '#6b7280' : '#9ca3af') : (theme ? '#1e293b' : '#f1f5f9')} 
          />
        </TouchableOpacity>
        
        <View style={{ flex: 1, alignItems: 'center' }}>
          {recording ? (
            <View style={{ alignItems: 'center' }}>
              <LottieView 
                source={require('@assets/animations/waveform.json')} 
                style={{ 
                  height: 60, 
                  width: 120,
                  alignSelf: "center" 
                }} 
                autoPlay 
                loop 
              />
              <Text style={{ 
                color: theme ? '#1e293b' : '#f1f5f9', 
                textAlign: 'center',
                fontSize: 14,
                fontWeight: '500',
                marginTop: -10
              }}>
                {message}
              </Text>
            </View>
          ) : (
            <Text style={{ 
                color: theme ? '#1e293b' : '#f1f5f9', 
              textAlign: 'center',
              fontSize: 16,
              fontWeight: '600'
            }}>
              Meeting note
            </Text>
          )}
        </View>
        
        <TouchableOpacity 
          style={Platform.OS === 'web'
            ? { ...globalStyles.modernActionButton, ...(recording ? (theme ? globalStyles.modernActionRed : globalStyles.modernActionRedDark) : (theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark)) }
            : [globalStyles.modernActionButton, recording ? (theme ? globalStyles.modernActionRed : globalStyles.modernActionRedDark) : (theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark)]
          }
          onPress={() => {
            if (recording) {
              setTimings(prev => ({ ...prev, endTime: new Date().getTime() }));
              stopRecording();
            } else {
              startRecording();
              setTimings({ startTime: new Date().getTime(), endTime: null })
            }
          }}
        >
          <MaterialCommunityIcons 
            name={recording ? "pause" : "record-circle-outline"} 
            size={28} 
            color="white"
          />
        </TouchableOpacity>
      </View>
    </View>
  ), [recording, message, theme]);

  const ModernSectionHeader = useCallback(({ title, isOpen, onToggle, count, showSort = false, onSortChange = undefined, currentSort = undefined }) => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginVertical: 8 }
      : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginVertical: 8 }]
    }>
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12
      }}>
        <TouchableOpacity onPress={onToggle} style={{ flex: 1 }}>
          <View>
            <Text style={{
              color: theme ? '#1e293b' : '#f1f5f9',
              fontWeight: '700',
              fontSize: 18
            }}>
              {title}
            </Text>
            {count !== undefined && (
              <Text style={{ 
                color: theme ? '#64748b' : '#94a3b8', 
                fontSize: 12,
                marginTop: 2
              }}>
                {count} items
              </Text>
            )}
          </View>
        </TouchableOpacity>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {showSort && onSortChange && (
            <Popover size="$5" allowFlip>
              <Popover.Trigger asChild>
                <TouchableOpacity
                  style={Platform.OS === 'web'
                    ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), paddingHorizontal: 12, paddingVertical: 8 }
                    : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { paddingHorizontal: 12, paddingVertical: 8 }]
                  }
                >
                  <MaterialIcons name="sort" size={20} color="white" />
                </TouchableOpacity>
              </Popover.Trigger>

              <Popover.Content
                borderWidth={1}
                borderColor="$borderColor"
                enterStyle={{ y: -10, opacity: 0 }}
                exitStyle={{ y: -10, opacity: 0 }}
                elevate
                animation={[
                  'quick',
                  {
                    opacity: {
                      overshootClamping: true,
                    },
                  },
                ]}
                padding="$3"
              >
                <YStack gap="$2" minWidth={200}>
                  <SizableText size="$3" fontWeight="700" paddingBottom="$2" paddingHorizontal="$2">
                    Sort by
                  </SizableText>
                  <Separator />

                  <TouchableOpacity onPress={() => onSortChange('date-new')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'date-new' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Date (Newest first)
                      </Text>
                      {currentSort === 'date-new' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => onSortChange('date-old')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'date-old' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Date (Oldest first)
                      </Text>
                      {currentSort === 'date-old' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => onSortChange('duration-long')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'duration-long' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Duration (Longest first)
                      </Text>
                      {currentSort === 'duration-long' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => onSortChange('duration-short')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'duration-short' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Duration (Shortest first)
                      </Text>
                      {currentSort === 'duration-short' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => onSortChange('title-az')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'title-az' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Title (A-Z)
                      </Text>
                      {currentSort === 'title-az' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity onPress={() => onSortChange('title-za')}>
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: currentSort === 'title-za' ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent'
                    }}>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14 }}>
                        Title (Z-A)
                      </Text>
                      {currentSort === 'title-za' && (
                        <MaterialIcons name="check" size={18} color={theme ? '#3b82f6' : '#60a5fa'} />
                      )}
                    </View>
                  </TouchableOpacity>
                </YStack>
              </Popover.Content>
            </Popover>
          )}

          <TouchableOpacity onPress={onToggle}>
            <MaterialIcons 
              name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"} 
              size={24} 
              color={theme ? '#64748b' : '#94a3b8'} 
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  ), [theme]);

  const LoadingStateComponent = useCallback(() => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), alignItems: 'center', padding: 24 }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24 }]
    }>
      <View style={{ alignItems: 'center', gap: 16 }}>
        <Spinner size="large" color="$blue10" />
        <Text style={{
          color: theme ? '#1e293b' : '#f1f5f9',
          fontSize: 16,
          fontWeight: '500'
        }}>
          Loading meetings...
        </Text>
      </View>
    </View>
  ), [theme]);

  const ErrorStateComponent = useCallback(() => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), alignItems: 'center', padding: 24 }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24 }]
    }>
      <View style={{ alignItems: 'center', gap: 16 }}>
        <MaterialIcons name="error-outline" size={48} color="#f44336" />
        <Text style={{ color: '#f44336', fontSize: 16, fontWeight: '600' }}>
          Failed to load meetings
        </Text>
        <TouchableOpacity 
          style={Platform.OS === 'web'
            ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), paddingHorizontal: 24, paddingVertical: 12 }
            : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { paddingHorizontal: 24, paddingVertical: 12 }]
          }
          onPress={() => refetch()}
        >
          <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>Retry</Text>
        </TouchableOpacity>
      </View>
    </View>
  ), [theme, refetch]);

  const EmptyMeetingsComponent = useCallback(() => (
    <View style={Platform.OS === 'web'
      ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark) }
      : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark]
    }>
      <View style={{ alignItems: 'center', gap: 16, padding: 24 }}>
        <MaterialIcons 
          name="event-available" 
          size={64} 
          color={theme ? '#6b7280' : '#9ca3af'} 
        />
        <Text style={{ 
          color: theme ? '#1e293b' : '#f1f5f9', 
          fontSize: 18,
          fontWeight: '600',
          textAlign: 'center'
        }}>
          No meetings yet
        </Text>
        <Text style={{ 
          color: theme ? '#64748b' : '#94a3b8', 
          fontSize: 14,
          textAlign: 'center',
          lineHeight: 20
        }}>
          Start recording above to create your first meeting summary
        </Text>
      </View>
    </View>
  ), [theme]);

  return (
    <DefaultScreen 
      transparentBackground={widget} 
      scrollDisabled
      noMargin
      containerStyle={{ paddingHorizontal: 30, paddingBottom: 100 }}
      style={{ ...globalStyles.modernMainContent }}
    >
      <YStack paddingHorizontal={10} paddingBottom={100} flex={1} gap="$4">
        {/* Modern Recording Section */}
        <ModernRecorder />

        {/* Queued Recordings Section */}
        <Animatable.View 
          useNativeDriver 
          transition={'flex'} 
          direction='alternate' 
          style={{ minHeight: 100, gap: 20, flex: myRecordingsOpen ? 4 : 0.5 }}
        >
          <ModernSectionHeader 
            title="Queued Recordings"
            isOpen={myRecordingsOpen}
            onToggle={() => setMyTemplatesOpen(!myRecordingsOpen)}
            count={recordings.length}
            showSort={false}
          />
          
          {myRecordingsOpen && (
            <>
              {!recordings.length ? (
                <EmptyRecordingsComponent />
              ) : (
                <FlatList
                  data={recordings}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  renderItem={renderRecordingItem}
                  keyExtractor={(item, index) => `recording-${index}`}
                  contentContainerStyle={{ paddingHorizontal: 8, paddingVertical: 8 }}
                />
              )}
            </>
          )}
        </Animatable.View>

        {/* Meetings Section */}
        <Animatable.View 
          useNativeDriver 
          transition={'flex'} 
          direction='alternate' 
          style={{ gap: 20, flex: meetingsOpen ? 8 : 1.5 }}
        >
          <ModernSectionHeader 
            title="Meetings"
            isOpen={meetingsOpen}
            onToggle={() => setMeetingsOpen(!meetingsOpen)}
            count={data?.length || 0}
            showSort={true}
            onSortChange={setSortOption}
            currentSort={sortOption}
          />
          
          {meetingsOpen && (
            <>
              {isLoading ? (
                <LoadingStateComponent />
              ) : error ? (
                <ErrorStateComponent />
              ) : !memoizedData?.length ? (
                <EmptyMeetingsComponent />
              ) : (
                <FlatList
                  data={memoizedData}
                  key={data}
                  style={{ flex: 1 }}
                  contentContainerStyle={{ paddingBottom: 100 }}
                  showsVerticalScrollIndicator={false}
                  refreshing={isFetching}
                  refreshControl={
                    <RefreshControl
                      refreshing={isFetching}
                      onRefresh={refetch}
                      tintColor={theme ? '#64748b' : '#94a3b8'}
                    />
                  }
                  renderItem={({ item, index }) => (
                    <View style={{ marginVertical: 4 }}>
                      <MeetingItem item={item} index={index} />
                    </View>
                  )}
                  keyExtractor={(item, index) => item._id || `meeting-${index}`}
                />
              )}
            </>
          )}
        </Animatable.View>
      </YStack>
    </DefaultScreen>
  );
}

const localStyles = StyleSheet.create({
  modernRecorderCard: {
    minHeight: 80,
    justifyContent: 'center',
  },
});
